import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Menu, X, User, LogOut, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { NotificationBell } from '@/components/NotificationBell';
import { InstallMenuEntry } from '@/components/InstallMenuEntry';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { translateServerError } from '@/lib/serverErrors';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL_DARK_TEXT = '/logo-ridalo.png';        // for light backgrounds
const LOGO_URL_LIGHT_TEXT = '/logo-ridalo-light.png'; // for dark backgrounds

export const Logo = ({ compact = false, variant = 'light-bg' }) => (
  <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
    <img
      src={variant === 'dark-bg' ? LOGO_URL_LIGHT_TEXT : LOGO_URL_DARK_TEXT}
      alt="Ridalo"
      className={compact ? 'h-8 w-auto' : 'h-10 w-auto'}
      loading="eager"
    />
  </Link>
);

export const Header = ({ transparent = false }) => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [converting, setConverting] = React.useState(false);
  const { t } = useTranslation();

  const handleBecomeProvider = async () => {
    // Not authenticated → normal signup path.
    if (!user) {
      navigate('/register?role=provider');
      return;
    }
    // Already a provider → go straight to dashboard.
    if (user.role === 'provider') {
      navigate('/dashboard');
      return;
    }
    // Authenticated traveler (Google or email/password): convert in place —
    // no second signup, no password prompt. The onboarding form still runs
    // afterwards to collect provider-specific info + documents.
    if (converting) return;
    setConverting(true);
    try {
      await axios.post(`${API}/user/become-provider`, {}, { withCredentials: true });
      await checkAuth();
      toast.success(t('auth.header.becomeProviderToast'));
      navigate('/dashboard');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? translateServerError(detail) : t('auth.header.convertError'));
    } finally {
      setConverting(false);
    }
  };

  return (
    <header className="glass-header" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          <Logo />

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-primary hover:text-accent transition-colors" data-testid="nav-home">{t('header.home')}</Link>
            <Link to="/services" className="text-sm font-medium text-primary hover:text-accent transition-colors" data-testid="nav-services">{t('header.services')}</Link>
            <Link to="/about" className="text-sm font-medium text-primary hover:text-accent transition-colors" data-testid="nav-about">{t('header.about')}</Link>
            <Link to="/faq" className="text-sm font-medium text-primary hover:text-accent transition-colors" data-testid="nav-faq">{t('header.faq')}</Link>
            <Link to="/contact" className="text-sm font-medium text-primary hover:text-accent transition-colors" data-testid="nav-contact">{t('header.contact')}</Link>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {user.role === 'client' && (
                  <button onClick={handleBecomeProvider} className="btn-primary text-sm py-2.5" data-testid="become-provider-button">
                    {t('header.becomeProvider')}
                  </button>
                )}
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 hover:border-accent transition-colors" data-testid="user-menu-button">
                      <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center text-white text-sm font-semibold">
                        {user.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <span className="text-sm font-medium text-primary hidden lg:inline">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200 shadow-lg">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      {t('header.dashboard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/account/notifications')} data-testid="notifications-settings-menu-item">
                      <Bell className="w-4 h-4 mr-2" />
                      {t('header.notificationsSettings')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('header.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="login-link">
                  <button className="text-sm font-medium text-primary hover:text-accent transition-colors px-3">{t('header.login')}</button>
                </Link>
                <button onClick={handleBecomeProvider} className="btn-primary text-sm py-2.5" data-testid="become-provider-button">
                  {t('header.becomeProvider')}
                </button>
              </>
            )}
            <InstallMenuEntry variant="desktop" />
            <LanguageSwitcher />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-1">
            {user && <NotificationBell />}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-primary"
              data-testid="mobile-menu-toggle"
              aria-label={t('header.menu')}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 mobile-menu-scroll overflow-y-auto overscroll-contain pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-home">{t('header.home')}</Link>
              <Link to="/services" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-services">{t('header.services')}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-about">{t('header.about')}</Link>
              <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-faq">{t('header.faq')}</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-contact">{t('header.contact')}</Link>
              <div className="border-t border-slate-100 pt-3 mt-2 flex flex-col gap-1">
                <InstallMenuEntry variant="mobile" />
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-dashboard">{t('header.dashboard')}</Link>
                    <Link to="/account/notifications" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-nav-notifications-settings">{t('header.notificationsSettings')}</Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-base font-medium text-red-600 text-left flex items-center min-h-11" data-testid="mobile-logout-button">
                      {t('header.logout')}
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-primary flex items-center min-h-11" data-testid="mobile-login-link">{t('header.login')}</Link>
                )}
                {(!user || user.role === 'client') && (
                  <button onClick={() => { handleBecomeProvider(); setMobileMenuOpen(false); }} className="btn-primary text-sm w-full min-h-12" data-testid="mobile-become-provider">
                    {t('header.becomeProvider')}
                  </button>
                )}
                <div className="pt-2 border-t border-slate-100">
                  <LanguageSwitcher variant="mobile" />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
